import { collection, doc, writeBatch } from "firebase/firestore";
import { db } from "../utils/FirebaseConfig";
import { savePosterMetadata } from "./posterRepository";

const PROFILE_FIELDS = new Set([
    "nama", "slug", "alamat", "poskod", "bandar", "daerah", "negeri",
    "zonSolat", "zone", "jenis", "logo", "logoJa", "gambar", "ogImage",
    "no_tel", "emel", "whatsapp", "facebook", "instagram", "youtube",
    "contactMode", "contactPreferred", "no_whatsapp", "gmaps",
    "latitude", "longitude", "qr", "pegawai", "bankAccounts",
]);

const boundedProfile = (profile = {}) => Object.fromEntries(
    Object.entries(profile).filter(([key]) => PROFILE_FIELDS.has(key))
);

export const createMosqueContentRepository = (dependencies) => {
    const {
        db: database,
        collection: collectionRef,
        doc: documentRef,
        writeBatch: makeBatch,
        savePosterMetadata: persistPoster,
    } = dependencies;

    const saveEvent = async (mosqueId, event, { legacyCollection = "kuliah" } = {}) => {
        const eventId = event?.eventId || event?.id;
        if (!mosqueId || !eventId) throw new TypeError("Event ID diperlukan");
        const normalized = {
            ...event,
            eventId,
            mosqueId,
            legacyCollection,
            writeSource: "normalized",
        };
        const batch = makeBatch(database);
        batch.set(
            documentRef(database, "masajid", mosqueId, "events", eventId),
            normalized,
            { merge: true }
        );
        batch.set(
            documentRef(database, "masajid", mosqueId, legacyCollection, eventId),
            normalized,
            { merge: true }
        );
        await batch.commit();
        return normalized;
    };

    const saveAnnouncement = async (mosqueId, announcement, { id = null } = {}) => {
        if (!mosqueId) throw new TypeError("Mosque ID diperlukan");
        const target = id
            ? documentRef(database, "masajid", mosqueId, "announcements", id)
            : documentRef(collectionRef(database, "masajid", mosqueId, "announcements"));
        const value = { ...announcement, announcementId: target.id };
        const batch = makeBatch(database);
        batch.set(target, value, { merge: true });
        await batch.commit();
        return { id: target.id, ...value };
    };

    const deleteEvent = async (mosqueId, eventId, { legacyCollection = "kuliah" } = {}) => {
        if (!mosqueId || !eventId) throw new TypeError("Event ID diperlukan");
        const batch = makeBatch(database);
        batch.delete(documentRef(database, "masajid", mosqueId, "events", eventId));
        batch.delete(documentRef(database, "masajid", mosqueId, legacyCollection, eventId));
        await batch.commit();
    };

    const saveSettings = async (mosqueId, settings) => {
        if (!mosqueId) throw new TypeError("Mosque ID diperlukan");
        const batch = makeBatch(database);
        batch.set(
            documentRef(database, "masajid", mosqueId, "settings", "main"),
            settings,
            { merge: true }
        );
        const writeId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        batch.set(
            documentRef(database, "masajid", mosqueId),
            { etv: settings, normalizedSettingsWriteId: writeId },
            { merge: true }
        );
        await batch.commit();
        return settings;
    };

    const saveProfile = async (mosqueId, profile) => {
        if (!mosqueId) throw new TypeError("Mosque ID diperlukan");
        const value = boundedProfile(profile);
        const batch = makeBatch(database);
        batch.set(
            documentRef(database, "masajid", mosqueId),
            { profail: value },
            { merge: true }
        );
        await batch.commit();
        return value;
    };

    return {
        saveEvent,
        deleteEvent,
        saveAnnouncement,
        savePoster: persistPoster,
        saveSettings,
        saveProfile,
    };
};

const repository = createMosqueContentRepository({
    db,
    collection,
    doc,
    writeBatch,
    savePosterMetadata,
});

export const saveEvent = repository.saveEvent;
export const deleteEvent = repository.deleteEvent;
export const saveAnnouncement = repository.saveAnnouncement;
export const savePoster = repository.savePoster;
export const saveSettings = repository.saveSettings;
export const saveProfile = repository.saveProfile;
